import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { delay, switchMap, takeUntil } from 'rxjs/operators';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { FESubscription } from '../../user.model';
import { UserService } from '../../service/user.abstract.service';
import { userRecordIdSelector } from '../../store/user.selectors';

@Component({
    selector: 'app-user-subscription-management-dialog',
    templateUrl: './user-subscription-management-dialog.component.html',
    styleUrls: ['./user-subscription-management-dialog.component.scss'],
})
export class SubscriptionManagementDialogComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();

    loading = false;
    userId: string;
    form: any;

    constructor(
        private translateService: TranslateService,
        private messageService: MessageService,
        private router: ActivatedRoute,
        private userService: UserService,
        private formBuilder: FormBuilder,
        private dialogRef: MatDialogRef<SubscriptionManagementDialogComponent>,
        private store: Store<State>,
        @Inject(MAT_DIALOG_DATA) public data: FESubscription,
    ) {}

    get startDate() {
        return new Date(this.data.startDate);
    }

    ngOnInit(): void {
        this.store
            .select(userRecordIdSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((id) => (this.userId = id));

        this.form = this.formBuilder.group(
            {
                endDate: new FormControl(this.data.endDate, [Validators.required]),
            },
            { updateOn: 'blur' },
        );
    }

    closeDialog() {
        this.dialogRef.close();
    }

    submit() {
        this.loading = true;
        const payload = {
            expirationDate: this.form.get('endDate').value,
        };
        this.userService
            .patchSubscription(this.userId, this.data._id, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.messageService.success(this.translateService.instant('pages.user-subscriptions.management-dialog.messages.success'));
                    this.dialogRef.close(true);

                    // Fire-and-forget: Update the card in the background after a delay
                    this.userService
                        .getWallet(this.userId)
                        .pipe(
                            delay(2000), // Wait 2 seconds for backend propagation
                            switchMap((wallet) => {
                                // Find the matching card by cardType
                                const matchingCard = wallet.cards.find((card) => card.cardType === this.data.cardType);

                                if (matchingCard) {
                                    const cardPayload = {
                                        expDate: matchingCard.expirationDate,
                                        birthDate: matchingCard.birthDate,
                                        firstName: matchingCard.firstName,
                                        lastName: matchingCard.lastName,
                                        photo: matchingCard.photo,
                                        address: matchingCard.address,
                                        subType: matchingCard.subType,
                                        sex: matchingCard.sex,
                                        cardTypes: [matchingCard.cardType],
                                    };

                                    return this.userService.patchUpdateCard(cardPayload, matchingCard._id);
                                }
                                return [];
                            }),
                        )
                        .subscribe(
                            () => {
                                // Success - no user feedback needed
                            },
                            (error) => {
                                // Log error for debugging but don't show to user
                                console.error('Background card update failed:', error);
                            },
                        );
                },
                (error) => {
                    this.loading = false;
                    this.messageService.error(this.translateService.instant('pages.user-subscriptions.management-dialog.messages.error'));
                },
            );
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
